// src/vatandas/pages/VatandasApplicationTrackingPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import "./ApplicationTrackingPage.css";

import {
  MagnifyingGlassIcon,
  PhoneIcon,
  HashtagIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowUturnLeftIcon,
  ClipboardDocumentCheckIcon,
  MapPinIcon,
  ChevronRightIcon,
  XMarkIcon,
  DocumentTextIcon,
  PhotoIcon,
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ArrowDownTrayIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

import { useDispatch, useSelector } from "react-redux";
import { fetchSharedDocuments } from "../redux/slices/vatandasTrackingSlice";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001/api";

/**
 * Public (no-auth) HTTP client:
 * - Tracking sayfası login/token’a bağlı olmamalı.
 * - Global axios interceptor’ları 401’de /giris’e atıyorsa burada devre dışı kalır.
 */
const httpPublic = axios.create({
  baseURL: BACKEND_URL,
  timeout: 15000,
});

/* =============================================================================
  Helpers
============================================================================= */

const normalizeListPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const normalizeErrorMessage = (err) => {
  const data = err?.response?.data;
  if (!data) return err?.message || "İşlem başarısız.";

  if (typeof data === "string") return data;
  if (data?.detail) return String(data.detail);

  const firstKey = Object.keys(data || {})[0];
  if (firstKey && Array.isArray(data[firstKey]) && data[firstKey][0]) {
    return String(data[firstKey][0]);
  }

  return "İşlem başarısız.";
};

const validatePhoneNumber = (phone) => /^[1-9]\d{9}$/.test(String(phone || "").trim());
const validateTrackingNumber = (tn) => String(tn || "").trim().length >= 4;

const formatDateTimeTR = (dateTime) => {
  if (!dateTime) return "-";
  const d = new Date(dateTime);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isClosedStatus = (status) => status === "çözüldü" || status === "çözülemedi";

const maskName = (value) => {
  const s = String(value || "").trim();
  if (!s) return "-";
  return `${s.slice(0, 2)}***`;
};

const maskPhone = (value) => {
  const raw = String(value || "").trim().replace(/\D/g, "");
  if (!raw) return "-";

  if (raw.length >= 10) {
    const d = raw.slice(-10);
    return `${d.slice(0, 2)}******${d.slice(-2)}`;
  }

  return `${raw.slice(0, 2)}***`;
};

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const buildOsmEmbedUrl = (lat, lng) => {
  const d = 0.0045;
  const left = lng - d;
  const right = lng + d;
  const top = lat + d;
  const bottom = lat - d;

  const bbox = `${left},${bottom},${right},${top}`;
  const marker = `${lat},${lng}`;

  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(
    bbox
  )}&layer=mapnik&marker=${encodeURIComponent(marker)}`;
};

const buildOsmOpenUrl = (lat, lng) =>
  `https://www.openstreetmap.org/?mlat=${encodeURIComponent(lat)}&mlon=${encodeURIComponent(
    lng
  )}#map=17/${encodeURIComponent(lat)}/${encodeURIComponent(lng)}`;

const BACKEND_ORIGIN = String(BACKEND_URL).replace(/\/api\/?$/, "");

const buildAbsoluteUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/")) return `${BACKEND_ORIGIN}${raw}`;
  return `${BACKEND_ORIGIN}/${raw}`;
};

const isImageUrl = (url) => /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(String(url || "").toLowerCase());

const normalizeMediaItem = (item, fallbackTitle) => {
  if (!item) return null;

  if (typeof item === "string") {
    const url = buildAbsoluteUrl(item);
    if (!url) return null;

    return {
      url,
      title: url.split("/").pop()?.split("?")[0] || fallbackTitle || "Dosya",
    };
  }

  if (typeof item === "object") {
    const rawUrl =
      item.url ||
      item.file ||
      item.path ||
      item.image ||
      item.document ||
      item.src ||
      "";

    const url = buildAbsoluteUrl(rawUrl);
    if (!url) return null;

    return {
      url,
      title:
        item.name ||
        item.filename ||
        item.title ||
        item.label ||
        fallbackTitle ||
        url.split("/").pop()?.split("?")[0] ||
        "Dosya",
    };
  }

  return null;
};

const dedupeMediaItems = (items) => {
  const seen = new Set();
  const out = [];

  for (const item of items || []) {
    if (!item?.url) continue;
    if (seen.has(item.url)) continue;
    seen.add(item.url);
    out.push(item);
  }

  return out;
};

const collectApplicationImages = (app) => {
  if (!app) return [];

  const explicit = [
    normalizeMediaItem(app?.image1, "Görsel 1"),
    normalizeMediaItem(app?.image2, "Görsel 2"),
  ].filter(Boolean);

  const pooled = [
    ...(Array.isArray(app?.images) ? app.images : []),
    ...(Array.isArray(app?.files) ? app.files : []),
  ]
    .map((item, idx) => normalizeMediaItem(item, `Görsel ${idx + 1}`))
    .filter(Boolean)
    .filter((item) => isImageUrl(item.url));

  return dedupeMediaItems([...explicit, ...pooled]);
};

const collectApplicationDocuments = (app) => {
  if (!app) return [];

  const explicit = [
    normalizeMediaItem(app?.document1, "Belge 1"),
    normalizeMediaItem(app?.document2, "Belge 2"),
  ].filter(Boolean);

  const pooled = [
    ...(Array.isArray(app?.documents) ? app.documents : []),
    ...(Array.isArray(app?.files) ? app.files : []),
  ]
    .map((item, idx) => normalizeMediaItem(item, `Belge ${idx + 1}`))
    .filter(Boolean)
    .filter((item) => !isImageUrl(item.url));

  return dedupeMediaItems([...explicit, ...pooled]);
};

const getPublicStatusLabel = (status) => {
  const s = String(status || "").toLowerCase();

  if (s === "yeni") return "Beklemede";
  if (s === "işleme_alındı") return "İncelemede";
  if (s === "yönlendirildi" || s === "başka birime yönlendirildi") return "Yönlendirildi";
  if (s === "çözüldü" || s === "çözülemedi") return "Sonuçlandırıldı";

  return "Güncellendi";
};

/* =============================================================================
  UI
============================================================================= */

function Toast({ toast, onClose }) {
  if (!toast) return null;

  const Icon =
    toast.type === "success"
      ? CheckCircleIcon
      : toast.type === "info"
      ? InformationCircleIcon
      : ExclamationTriangleIcon;

  return (
    <div className={`vtr-toast ${toast.type}`} role="status" aria-live="polite">
      <Icon className="vtr-toast-ic" aria-hidden="true" />
      <div className="vtr-toast-body">
        <div className="vtr-toast-title">{toast.title}</div>
        <div className="vtr-toast-text">{toast.message}</div>
      </div>
      <button className="vtr-toast-close" onClick={onClose} aria-label="Kapat">
        <XMarkIcon className="vtr-toast-close-ic" />
      </button>
    </div>
  );
}

function MediaPreviewModal({ open, type, item, onClose }) {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!open) return undefined;
    setScale(1);

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || !item?.url) return null;

  const ct = String(item.contentType || "").toLowerCase();
  const isImage = type === "image" || ct.startsWith("image/");
  const isPdf = ct === "application/pdf" || /\.pdf(\?|$)/i.test(String(item.url || ""));
  const title = item.title || (isImage ? "Görsel" : "Belge");
  const downloadHref = item.downloadUrl || item.url;

  const zoomIn = () => setScale((s) => Math.min(5, +(s + 0.25).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(0.25, +(s - 0.25).toFixed(2)));
  const zoomReset = () => setScale(1);

  return (
    <div className="vtr-media-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="vtr-media-backdrop" onClick={onClose} />
      <div className="vtr-media-dialog">
        <div className="vtr-media-head">
          <div className="vtr-media-title">{title}</div>
          <div className="vtr-media-head-actions">
            {isImage ? (
              <>
                <button className="vtr-media-zoom-btn" type="button" onClick={zoomOut} aria-label="Küçült">−</button>
                <button className="vtr-media-zoom-btn" type="button" onClick={zoomReset} aria-label="Sıfırla">
                  {Math.round(scale * 100)}%
                </button>
                <button className="vtr-media-zoom-btn" type="button" onClick={zoomIn} aria-label="Büyüt">+</button>
              </>
            ) : null}
            <a className="vtr-media-dl" href={downloadHref} target="_blank" rel="noopener noreferrer" download>
              İndir
            </a>
            <button className="vtr-media-close" type="button" onClick={onClose} aria-label="Kapat">
              <XMarkIcon className="vtr-media-close-ic" />
            </button>
          </div>
        </div>

        <div
          className="vtr-media-body"
          onWheel={
            isImage
              ? (e) => {
                  if (e.deltaY < 0) zoomIn();
                  else zoomOut();
                }
              : undefined
          }
        >
          {isImage ? (
            <img
              src={item.url}
              alt={title}
              className="vtr-media-image"
              style={{ transform: `scale(${scale})`, transformOrigin: "center center", transition: "transform 0.1s" }}
            />
          ) : isPdf ? (
            <iframe title={title} src={item.url} className="vtr-media-frame" />
          ) : (
            <div className="vtr-media-fallback">
              <DocumentTextIcon className="vtr-media-fallback-ic" aria-hidden="true" />
              <div className="vtr-media-fallback-title">{title}</div>
              <a
                href={downloadHref}
                target="_blank"
                rel="noopener noreferrer"
                className="vtr-media-open-link"
              >
                Yeni sekmede aç
                <ArrowTopRightOnSquareIcon className="vtr-media-open-link-ic" aria-hidden="true" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =============================================================================
  Page
============================================================================= */

export default function VatandasApplicationTrackingPage() {
  const location = useLocation();

  const tokenFromUrl = useMemo(() => {
    const qs = new URLSearchParams(location.search || "");
    return String(qs.get("t") || "").trim();
  }, [location.search]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [phone, setPhone] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");

  const [loadingTrack, setLoadingTrack] = useState(false);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  const [loadingTokenEntry, setLoadingTokenEntry] = useState(false);
  const [initialEntryResolved, setInitialEntryResolved] = useState(false);
  const [tokenConsumed, setTokenConsumed] = useState(false);

  const [application, setApplication] = useState(null);
const [statusLogs, setStatusLogs] = useState([]);
const [smsHistory, setSmsHistory] = useState([]);
const [expandedSmsKeys, setExpandedSmsKeys] = useState({});
const dispatch = useDispatch();
const sharedDocuments = useSelector((s) => s.vatandasTracking?.sharedDocuments || []);

  const [toast, setToast] = useState(null);

  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [toast]);

  const showToast = useCallback((type, title, message) => {
    setToast({ type, title, message });
  }, []);
const toggleSmsItem = useCallback((key) => {
  setExpandedSmsKeys((prev) => ({
    ...prev,
    [key]: !prev[key],
  }));
}, []);
  const openPreview = useCallback((type, item) => {
    if (!item?.url) return;
    setPreviewType(type);
    setPreviewItem(item);
    setPreviewOpen(true);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewType(null);
    setPreviewItem(null);
  }, []);

  /* ---------------------------------------------------------------------------
    Units
  --------------------------------------------------------------------------- */

  const fetchUnits = useCallback(async () => {
    if (loadingUnits) return;
    if (units?.length) return;

    setLoadingUnits(true);
    try {
      const res = await httpPublic.get("/core/units/");
      if (!mountedRef.current) return;
      setUnits(normalizeListPayload(res.data));
    } catch {
      // sessiz
    } finally {
      if (mountedRef.current) setLoadingUnits(false);
    }
  }, [loadingUnits, units?.length]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  const unitsById = useMemo(() => {
    const m = new Map();
    for (const u of units || []) {
      const id = Number(u?.id);
      if (Number.isFinite(id) && u?.name) m.set(id, u.name);
    }
    return m;
  }, [units]);

  /* ---------------------------------------------------------------------------
    Derived
  --------------------------------------------------------------------------- */

  const citizen = application?.citizen || {};

  const unitName = useMemo(() => {
    if (!application) return "-";

    if (application?.unit && typeof application.unit === "object") {
      return application.unit?.name || "-";
    }

    const direct =
      application?.unit_name ||
      application?.unit_display ||
      application?.unitLabel ||
      application?.unitName;

    if (direct) return direct;

    const id = Number(application?.unit);
    if (Number.isFinite(id)) return unitsById.get(id) || `#${id}`;

    return application?.unit ? String(application.unit) : "-";
  }, [application, unitsById]);

  const coords = useMemo(() => {
    if (!application) return null;

    const lat =
      safeNumber(application?.latitude) ??
      safeNumber(application?.lat) ??
      safeNumber(application?.location_lat) ??
      null;

    const lng =
      safeNumber(application?.longitude) ??
      safeNumber(application?.lng) ??
      safeNumber(application?.long) ??
      safeNumber(application?.location_lng) ??
      null;

    if (lat == null || lng == null) return null;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    if (lat === 0 && lng === 0) return null;

    return { lat, lng };
  }, [application]);

  const imageAttachments = useMemo(() => collectApplicationImages(application), [application]);
  const documentAttachments = useMemo(() => collectApplicationDocuments(application), [application]);
  const hasAttachmentBlock = imageAttachments.length > 0 || documentAttachments.length > 0;

  const statusLower = String(application?.status || "").toLowerCase();
  const closed = isClosedStatus(statusLower);
  const statusDisplayLabel = useMemo(
    () => getPublicStatusLabel(application?.status),
    [application?.status]
  );

  const statusBadge = useMemo(() => {
    if (closed) {
      return { label: "Son güncelleme", Icon: InformationCircleIcon, tone: "info" };
    }

    const map = {
      yeni: { label: "Beklemede", Icon: ArrowPathIcon, tone: "info" },
      işleme_alındı: { label: "İncelemeye Alındı", Icon: ClipboardDocumentCheckIcon, tone: "success" },
      yönlendirildi: { label: "Yönlendirildi", Icon: ClipboardDocumentCheckIcon, tone: "info" },
      "başka birime yönlendirildi": {
        label: "Yönlendirildi",
        Icon: ClipboardDocumentCheckIcon,
        tone: "info",
      },
    };

    return map[statusLower] || { label: "Güncellendi", Icon: InformationCircleIcon, tone: "info" };
  }, [closed, statusLower]);

  const detailCards = useMemo(() => {
    const cards = [];

    cards.push({
      key: "contact",
      className: "vtr-block vtr-block--contact",
      content: (
        <>
          <div className="vtr-block-title">
            <PhoneIcon className="vtr-block-title-ic" aria-hidden="true" />
            <span>İletişim Bilgileri</span>
          </div>

          <div className="vtr-kv">
            <div className="vtr-k">İsim</div>
            <div className="vtr-v">{maskName(citizen?.name)}</div>
          </div>

          <div className="vtr-kv">
            <div className="vtr-k">Soyisim</div>
            <div className="vtr-v">{maskName(citizen?.surname)}</div>
          </div>

          <div className="vtr-kv">
            <div className="vtr-k">Telefon</div>
            <div className="vtr-v">{maskPhone(citizen?.phone_number)}</div>
          </div>
        </>
      ),
    });

    cards.push({
      key: "application",
      className: "vtr-block vtr-block--application",
      content: (
        <>
          <div className="vtr-block-title">
            <ClipboardDocumentCheckIcon className="vtr-block-title-ic" aria-hidden="true" />
            <span>Başvuru Bilgileri</span>
          </div>

          <div className="vtr-kv">
            <div className="vtr-k">Takip No</div>
            <div className="vtr-v">{application?.tracking_number || "-"}</div>
          </div>

          <div className="vtr-kv">
            <div className="vtr-k">Durum</div>
            <div className="vtr-v">{statusDisplayLabel}</div>
          </div>

          <div className="vtr-kv">
            <div className="vtr-k">İlgili Birim</div>
            <div className="vtr-v">
              {loadingUnits && !unitName ? "Yükleniyor…" : unitName || "-"}
            </div>
          </div>

          <div className="vtr-kv">
            <div className="vtr-k">Konu</div>
            <div className="vtr-v">{application?.subject || "-"}</div>
          </div>

          <div className="vtr-kv">
            <div className="vtr-k">Açıklama</div>
            <div className="vtr-v vtr-v--wrap">{application?.description || "-"}</div>
          </div>

          <div className="vtr-kv">
            <div className="vtr-k">Adres</div>
            <div className="vtr-v vtr-v--wrap">{application?.address || "Adres belirtilmemiş"}</div>
          </div>

          <div className="vtr-kv">
            <div className="vtr-k">Son Güncelleme</div>
            <div className="vtr-v">
              {formatDateTimeTR(application?.updated_at || application?.status_date)}
            </div>
          </div>
        </>
      ),
    });

    if (hasAttachmentBlock) {
      cards.push({
        key: "attachments",
        className: "vtr-block vtr-block--attachments",
        content: (
          <>
            <div className="vtr-block-title">
              <DocumentTextIcon className="vtr-block-title-ic" aria-hidden="true" />
              <span>Ekler</span>
            </div>

            {imageAttachments.length ? (
              <div className="vtr-attachment-section">
                <div className="vtr-attachment-label">
                  <PhotoIcon className="vtr-attachment-label-ic" aria-hidden="true" />
                  <span>Görseller</span>
                </div>

                <div className="vtr-image-grid">
                  {imageAttachments.map((item, idx) => (
                    <button
                      key={`img-${item.url}-${idx}`}
                      type="button"
                      className="vtr-image-thumb"
                      onClick={() => openPreview("image", item)}
                    >
                      <span className="vtr-image-thumb-media">
                        <img src={item.url} alt={item.title || `Görsel ${idx + 1}`} />
                      </span>
                      <span className="vtr-image-thumb-title">
                        {item.title || `Görsel ${idx + 1}`}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {documentAttachments.length ? (
              <div className="vtr-attachment-section">
                <div className="vtr-attachment-label">
                  <DocumentTextIcon className="vtr-attachment-label-ic" aria-hidden="true" />
                  <span>Belgeler</span>
                </div>

                <div className="vtr-doc-grid">
                  {documentAttachments.map((item, idx) => (
                    <button
                      key={`doc-${item.url}-${idx}`}
                      type="button"
                      className="vtr-doc-card"
                      onClick={() => openPreview("document", item)}
                    >
                      <DocumentTextIcon className="vtr-doc-card-ic" aria-hidden="true" />
                      <span className="vtr-doc-card-title">
                        {item.title || `Belge ${idx + 1}`}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        ),
      });
    }

    if (Array.isArray(sharedDocuments) && sharedDocuments.length) {
      cards.push({
        key: "shared-documents",
        className: "vtr-block vtr-block--attachments",
        content: (
          <>
            <div className="vtr-block-title">
              <DocumentTextIcon className="vtr-block-title-ic" aria-hidden="true" />
              <span>Tarafınıza İletilen Belgeler</span>
            </div>
            <div className="vtr-sdoc-hint">
              Belediye tarafından başvurunuza iletilen belgeler. Görüntülemek veya indirmek için dokunun.
            </div>

            <div className="vtr-attachment-section">
              <div className="vtr-sdoc-grid">
                {sharedDocuments.map((doc, idx) => {
                  const isImg = String(doc.content_type || "").toLowerCase().startsWith("image/");
                  const base = `${BACKEND_ORIGIN}${doc.download_url}`;
                  const previewUrl = `${base}${base.includes("?") ? "&" : "?"}inline=1`;
                  const title = doc.title || `Belge ${idx + 1}`;
                  return (
                    <div className="vtr-sdoc-card" key={`shared-${doc.id ?? idx}`}>
                      <button
                        type="button"
                        className="vtr-sdoc-open"
                        title={`${title} — görüntüle`}
                        onClick={() =>
                          openPreview(isImg ? "image" : "document", {
                            url: previewUrl,
                            downloadUrl: base,
                            title,
                            contentType: doc.content_type,
                          })
                        }
                      >
                        {isImg ? (
                          <img className="vtr-sdoc-thumb" src={previewUrl} alt={title} loading="lazy" />
                        ) : (
                          <span className="vtr-sdoc-thumb vtr-sdoc-thumb--doc">
                            <DocumentTextIcon aria-hidden="true" />
                          </span>
                        )}
                        <span className="vtr-sdoc-overlay" aria-hidden="true">
                          <EyeIcon />
                        </span>
                      </button>
                      <div className="vtr-sdoc-foot">
                        <span className="vtr-sdoc-name" title={title}>
                          {title}
                        </span>
                        <a
                          className="vtr-sdoc-dl"
                          href={base}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          title="İndir"
                          aria-label="İndir"
                        >
                          <ArrowDownTrayIcon />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ),
      });
    }

    if (coords) {
      cards.push({
        key: "map",
        className: "vtr-block vtr-block--map",
        content: (
          <>
            <div className="vtr-block-title">
              <MapPinIcon className="vtr-block-title-ic" aria-hidden="true" />
              <span>Konum</span>
            </div>

            <div className="vtr-map-head">
              <a
                className="vtr-map-open"
                href={buildOsmOpenUrl(coords.lat, coords.lng)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Haritada aç
                <ChevronRightIcon className="vtr-map-open-ic" aria-hidden="true" />
              </a>
            </div>

            <div className="vtr-map-frame">
              <iframe
                title="Konum Haritası"
                src={buildOsmEmbedUrl(coords.lat, coords.lng)}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </>
        ),
      });
    }

    return cards;
  }, [
    application,
    citizen?.name,
    citizen?.surname,
    citizen?.phone_number,
    coords,
    documentAttachments,
    hasAttachmentBlock,
    imageAttachments,
    loadingUnits,
    openPreview,
    sharedDocuments,
    statusDisplayLabel,
    unitName,
  ]);

  /* ---------------------------------------------------------------------------
    API calls
  --------------------------------------------------------------------------- */

  const trackPublic = useCallback(async ({ phoneNumber, tracking }) => {
    return httpPublic.post("/core/applications/track/", {
      phone_number: phoneNumber,
      tracking_number: tracking,
    });
  }, []);

  const trackByLinkPublic = useCallback(async ({ token }) => {
    return httpPublic.get("/core/applications/track-by-link/", {
      params: { t: token },
    });
  }, []);

  const fetchStatusLogsPublic = useCallback(async ({ token, phoneNumber, tracking }) => {
    // Public "Hareketler" (status log) — SMS geçmişi endpoint'iyle AYNI
    // public sözleşme:
    //   - token (?t=)                      → SMS takip-linki akışı (telefon YOK)
    //   - tracking_number + phone_number   → manuel sorgu akışı
    // NOT: Eski ?application= yolu bu public sayfada HER ZAMAN 401'di
    // (RBAC/FAZ-10 ile login zorunlu oldu) → status log'lar yüklenemiyor,
    // vatandaş yalnız "Başvuru alındı" görüyordu. Token yolu bunu çözer.
    return httpPublic.get("/staff/status-logs/", {
      params: token
        ? { t: token }
        : { tracking_number: tracking, phone_number: phoneNumber },
    });
  }, []);

  const fetchPublicSmsHistoryPublic = useCallback(
  async ({ token, trackingNumber, phoneNumber }) => {
    return httpPublic.get("/notifications/notifications/public-application-sms/", {
      params: token
        ? { t: token }
        : {
            tracking_number: trackingNumber,
            phone_number: phoneNumber,
          },
    });
  },
  []
);

  /* ---------------------------------------------------------------------------
    Submit / Reset
  --------------------------------------------------------------------------- */

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setToast(null);

      const p = String(phone || "").trim();
      const t = String(trackingNumber || "").trim();

      if (!validatePhoneNumber(p)) {
        showToast("info", "Telefon hatalı", "Geçerli telefon numarası giriniz (10 hane, 5XXXXXXXXX).");
        return;
      }

      if (!validateTrackingNumber(t)) {
        showToast("info", "Takip numarası eksik", "Lütfen takip numaranızı giriniz.");
        return;
      }
setLoadingTrack(true);
setApplication(null);
setStatusLogs([]);
setSmsHistory([]);
setExpandedSmsKeys({});

      try {
        const res = await trackPublic({ phoneNumber: p, tracking: t });
        if (!mountedRef.current) return;

        const app = res?.data || null;
        if (!app) {
          showToast("error", "Bulunamadı", "Başvuru bulunamadı. Bilgileri kontrol edip tekrar deneyin.");
          return;
        }

        setApplication(app);
      } catch (err) {
        if (!mountedRef.current) return;
        showToast("error", "Sorgulanamadı", normalizeErrorMessage(err));
      } finally {
        if (mountedRef.current) setLoadingTrack(false);
      }
    },
    [phone, trackingNumber, showToast, trackPublic]
  );

  const handleReset = useCallback(() => {
  setToast(null);
  setPhone("");
  setTrackingNumber("");
  setApplication(null);
  setStatusLogs([]);
  setSmsHistory([]);
  setExpandedSmsKeys({});
  closePreview();

  setLoadingTokenEntry(false);
  setInitialEntryResolved(true);
  setTokenConsumed(true);
}, [closePreview]);

  useEffect(() => {
    const run = async () => {
      if (!tokenFromUrl || tokenConsumed) {
        setInitialEntryResolved(true);
        return;
      }

      setLoadingTokenEntry(true);
      setToast(null);

      try {
        const res = await trackByLinkPublic({ token: tokenFromUrl });
        if (!mountedRef.current) return;

        const app = res?.data || null;
        if (!app) {
          showToast("error", "Geçersiz bağlantı", "Takip bağlantısı ile başvuru açılamadı.");
          setInitialEntryResolved(true);
          setTokenConsumed(true);
          return;
        }

        setStatusLogs([]);
setSmsHistory([]);
setExpandedSmsKeys({});
setApplication(app);

        if (app?.tracking_number) {
          setTrackingNumber(String(app.tracking_number));
        }

        setInitialEntryResolved(true);
        setTokenConsumed(true);
      } catch (err) {
        if (!mountedRef.current) return;
        showToast("error", "Bağlantı geçersiz", normalizeErrorMessage(err));
        setInitialEntryResolved(true);
        setTokenConsumed(true);
      } finally {
        if (mountedRef.current) {
          setLoadingTokenEntry(false);
        }
      }
    };

    run();
  }, [tokenFromUrl, tokenConsumed, trackByLinkPublic, showToast]);

  /* ---------------------------------------------------------------------------
    Timeline data
  --------------------------------------------------------------------------- */

  useEffect(() => {
  const run = async () => {
    if (!application?.id) return;

    const p = String(phone || "").trim();
    const t = String(trackingNumber || "").trim();

    setLoadingTimeline(true);

    try {
      const logsPromise = fetchStatusLogsPublic(
        tokenFromUrl
          ? { token: tokenFromUrl }
          : { tracking: t, phoneNumber: p }
      );

      const smsPromise = fetchPublicSmsHistoryPublic(
        tokenFromUrl
          ? { token: tokenFromUrl }
          : {
              trackingNumber: t,
              phoneNumber: p,
            }
      );

      const [logsRes, smsRes] = await Promise.all([logsPromise, smsPromise]);

      if (!mountedRef.current) return;

      const logs = normalizeListPayload(logsRes?.data);
      const smsItems = normalizeListPayload(smsRes?.data);

      setStatusLogs(logs);
      setSmsHistory(smsItems);
      setExpandedSmsKeys({});

      // Vatandaşa iletilen belgeler — SLICE üzerinden (fire-and-forget; logs/sms akışını bozmaz).
      dispatch(
        fetchSharedDocuments(
          tokenFromUrl ? { token: tokenFromUrl } : { trackingNumber: t, phoneNumber: p }
        )
      );
    } catch {
      // sessiz
    } finally {
      if (mountedRef.current) setLoadingTimeline(false);
    }
  };

  run();
}, [
  application,
  phone,
  trackingNumber,
  tokenFromUrl,
  fetchStatusLogsPublic,
  fetchPublicSmsHistoryPublic,
  dispatch,
]);

  /* ---------------------------------------------------------------------------
    Timeline events
  --------------------------------------------------------------------------- */
const timelineEvents = useMemo(() => {
  const events = [];

  const createdAt = application?.created_at || application?.created || null;
  if (createdAt) {
    events.push({
      type: "created",
      title: "Başvuru alındı",
      at: createdAt,
      meta: null,
    });
  }

  for (const log of statusLogs || []) {
    const st = String(log?.status || "");
    const at = log?.change_date || log?.created_at || log?.created || null;
    if (!st || !at) continue;

    const s = st.toLowerCase();

    let title = "Güncelleme";
    if (s === "işleme_alındı") title = "İncelemeye alındı";
    else if (s === "yönlendirildi" || s === "başka birime yönlendirildi") title = "Yönlendirildi";
    else if (s === "çözüldü" || s === "çözülemedi") title = "Sonuçlandırıldı";

    events.push({
      type: "status",
      title,
      at,
      meta: String(log?.note || "").trim()
        ? { note: String(log.note).trim() }
        : null,
    });
  }

  for (const sms of smsHistory || []) {
    const at = sms?.sent_at || sms?.created_at || sms?.updated_at || null;
    const message = String(sms?.message || "").trim();

    if (!at || !message) continue;

    events.push({
      type: "sms",
      title: "SMS gönderildi",
      at,
      meta: {
        smsKey: `sms-${sms?.id || at}`,
        message,
      },
    });
  }

  // Belgeler tarafınıza iletildi — sonuç (çözüldü/çözülemedi) adımının hemen ÖNCESİNDE
  if (Array.isArray(sharedDocuments) && sharedDocuments.length) {
    let finalAt = null;
    for (const log of statusLogs || []) {
      const s = String(log?.status || "").toLowerCase();
      if (s === "çözüldü" || s === "çözülemedi") {
        finalAt = log?.change_date || log?.created_at || log?.created || finalAt;
      }
    }
    const base = finalAt || application?.status_date || application?.updated_at || null;
    if (base) {
      // sonuçtan ~1sn önce → sıralamada sonucun hemen öncesine düşer
      const at = new Date(new Date(base).getTime() - 1000).toISOString();
      events.push({
        type: "document",
        title: "Belgeler tarafınıza iletildi",
        at,
        meta: null,
      });
    }
  }

  const sorted = events
    .filter((x) => x?.at)
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  const seen = new Set();
  const deduped = [];

  for (const ev of sorted) {
    const key = [
      ev.type,
      ev.at,
      ev.title,
      ev?.meta?.note || "",
      ev?.meta?.message || "",
      ev?.meta?.smsKey || "",
    ].join("|");

    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(ev);
  }

  return deduped;
}, [application, statusLogs, smsHistory, sharedDocuments]);

  /* =============================================================================
    Render
  ============================================================================= */

  const showInitialTokenLoading = Boolean(tokenFromUrl) && !initialEntryResolved;
  const showForm = !application && !showInitialTokenLoading;
  const showDetails = Boolean(application);

  return (
    <div className="vtr-page">
      <div className="vtr-shell">
        {showInitialTokenLoading ? (
          <div className="vtr-center">
            <div className="vtr-form v-card">
              <div className="vtr-form-title">
                <ArrowPathIcon className="vtr-form-title-ic spin" aria-hidden="true" />
                <span>Başvurunuz Açılıyor</span>
              </div>

              <div className="vtr-fields">
                <div className="vtr-hint">Takip bağlantınız doğrulanıyor. Lütfen bekleyiniz…</div>
              </div>
            </div>
          </div>
        ) : showForm ? (
          <div className="vtr-center">
            <form className="vtr-form v-card" onSubmit={handleSubmit}>
              <div className="vtr-form-title">
                <MagnifyingGlassIcon className="vtr-form-title-ic" aria-hidden="true" />
                <span>Başvuru Sorgula</span>
              </div>

              <div className="vtr-fields">
                <div className="vtr-field">
                  <label className="vtr-label" htmlFor="vtr-phone">
                    Telefon Numarası <span className="vtr-req">*</span>
                  </label>
                  <div className="vtr-input-wrap">
                    <PhoneIcon className="vtr-input-ic" aria-hidden="true" />
                    <input
                      id="vtr-phone"
                      className="v-input vtr-input"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="5XXXXXXXXX"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={loadingTrack}
                      required
                    />
                  </div>
                  <div className="vtr-hint">Başında 0 olmadan 10 hane.</div>
                </div>

                <div className="vtr-field">
                  <label className="vtr-label" htmlFor="vtr-tracking">
                    Takip Numarası <span className="vtr-req">*</span>
                  </label>
                  <div className="vtr-input-wrap">
                    <HashtagIcon className="vtr-input-ic" aria-hidden="true" />
                    <input
                      id="vtr-tracking"
                      className="v-input vtr-input"
                      type="text"
                      placeholder="Takip numaranız"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      disabled={loadingTrack}
                      required
                    />
                  </div>
                </div>

                <button className="v-btn v-btn--primary vtr-submit" type="submit" disabled={loadingTrack}>
                  {loadingTrack ? (
                    <>
                      <ArrowPathIcon className="vtr-btn-ic spin" aria-hidden="true" />
                      Sorgulanıyor…
                    </>
                  ) : (
                    <>
                      <MagnifyingGlassIcon className="vtr-btn-ic" aria-hidden="true" />
                      Sorgula
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        ) : showDetails ? (
          <div className="vtr-details v-card">
            <div className="vtr-details-head">
              <div className={`vtr-status-badge ${statusBadge.tone}`}>
                <statusBadge.Icon className="vtr-status-ic" aria-hidden="true" />
                <span className="vtr-status-text">{statusBadge.label}</span>
              </div>

              <button className="v-btn vtr-reset" type="button" onClick={handleReset}>
                <ArrowUturnLeftIcon className="vtr-btn-ic" aria-hidden="true" />
                Yeni Sorgu
              </button>
            </div>

            <div className="vtr-details-grid">
              {detailCards.map((card) => (
                <div key={card.key} className={card.className}>
                  {card.content}
                </div>
              ))}
            </div>

            <div className="vtr-timeline">
              <div className="vtr-timeline-title">
                <ClipboardDocumentCheckIcon className="vtr-timeline-title-ic" aria-hidden="true" />
                <span>Hareketler</span>
              </div>

              {loadingTimeline ? (
                <div className="vtr-timeline-empty">
                  <ArrowPathIcon className="vtr-mini-ic spin" aria-hidden="true" />
                  <span>Yükleniyor…</span>
                </div>
              ) : timelineEvents?.length ? (
                <div className="vtr-timeline-list">
                  {timelineEvents.map((ev, idx) => (
                    <div className="vtr-timeline-item" key={`${ev.type}-${ev.at}-${idx}`}>
                      <div className="vtr-timeline-dot" aria-hidden="true" />
                      <div className="vtr-timeline-body">
                        <div className="vtr-timeline-row">
                          <div className="vtr-timeline-what">{ev.title}</div>
                          <div className="vtr-timeline-when">{formatDateTimeTR(ev.at)}</div>
                        </div>
{ev?.type === "sms" && ev?.meta?.message ? (
  <div className="vtr-timeline-sms">
    <button
      type="button"
      className="vtr-timeline-sms-toggle"
      onClick={() => toggleSmsItem(ev.meta.smsKey)}
      aria-expanded={Boolean(expandedSmsKeys[ev.meta.smsKey])}
    >
      <span className="vtr-timeline-sms-toggle-left">
        <ChatBubbleLeftRightIcon className="vtr-timeline-sms-ic" aria-hidden="true" />
        <span>SMS içeriğini göster</span>
      </span>

      <ChevronDownIcon
        className={`vtr-timeline-sms-chevron ${
          expandedSmsKeys[ev.meta.smsKey] ? "is-open" : ""
        }`}
        aria-hidden="true"
      />
    </button>

    {expandedSmsKeys[ev.meta.smsKey] ? (
      <div className="vtr-timeline-note vtr-timeline-note--sms">
        <div className="vtr-timeline-note-label">Gönderilen SMS</div>
        <div className="vtr-timeline-sms-text">{ev.meta.message}</div>
      </div>
    ) : null}
  </div>
) : ev?.meta?.note ? (
  <div className="vtr-timeline-note">
    {ev.title === "Sonuçlandırıldı" ? (
      <>
        <div className="vtr-timeline-note-label">Vatandaşa iletilen not</div>
        <div>{ev.meta.note}</div>
      </>
    ) : (
      ev.meta.note
    )}
  </div>
) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="vtr-timeline-empty">
                  <InformationCircleIcon className="vtr-mini-ic" aria-hidden="true" />
                  <span>Hareket kaydı bulunamadı.</span>
                </div>
              )}
            </div>

            {closed ? (
              <div className="vtr-survey">
                <div className="vtr-survey-left">
                  <ClipboardDocumentCheckIcon className="vtr-survey-ic" aria-hidden="true" />
                  <div className="vtr-survey-text">
                    <div className="vtr-survey-title">Memnuniyet Anketi</div>
                    <div className="vtr-survey-sub">
                      Hizmet kalitesini geliştirmemize yardımcı olmak için geri bildiriminizi paylaşabilirsiniz.
                    </div>
                  </div>
                </div>

                <Link className="vtr-survey-link" to="/memnuniyet-anketi">
                  Ankete Git
                  <ChevronRightIcon className="vtr-survey-link-ic" aria-hidden="true" />
                </Link>
              </div>
            ) : null}
          </div>
        ) : null}

        <MediaPreviewModal
          open={previewOpen}
          type={previewType}
          item={previewItem}
          onClose={closePreview}
        />

        <Toast toast={toast} onClose={() => setToast(null)} />
      </div>
    </div>
  );
}